import { useState } from "react";
import { useForm } from "react-hook-form";

import type { AxiosError } from "axios";

import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import {
	isValidEmail,
	invalidEmailErrorMessage,
} from "@libs/utils/validators/email";

import { identityUpdateContactSendCode } from "@libs/acp/apis/updateAccount/updateAccountAPI";

type FormValues = {
	email: string;
};

const schema = z.object({
	email: isValidEmail,
});

export const useUpdateEmailModal = (
	onClose: () => void,
	onSuccess: (newEmail: string) => void,
) => {
	const [showSuccessToast, setShowSuccessToast] = useState(false);

	const form = useForm<FormValues>({
		defaultValues: { email: "" },
		mode: "onSubmit",
		resolver: zodResolver(schema),
	});

	const handleClose = () => {
		form.reset();
		onClose();
	};

	const onSubmit = async (data: FormValues) => {
		try {
			await identityUpdateContactSendCode({
				type: "email",
				contact: data.email,
				flow_type: "checkout",
			});

			handleClose();
			onSuccess(data.email);
			setShowSuccessToast(true);
		} catch (err) {
			const error = err as AxiosError<{ message?: string }>;

			const errorMessage =
				error.response?.data?.message || invalidEmailErrorMessage;

			form.setError("email", {
				type: "manual",
				message: errorMessage,
			});
		}
	};

	return {
		form,
		showSuccessToast,
		setShowSuccessToast,
		handleClose,
		onSubmit,
	};
};
